import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import Select, { type SelectProps } from '@/components/common/Select';
import { getInputState } from '@/components/fields/InputField';

export interface SelectFieldProps extends Omit<SelectProps, 'onChange' | 'errorElement' | 'inputState' | 'value'> {
    name: string;
}

const SelectField = ({ name, ...props }: SelectFieldProps) => {
    const { register, setValue, trigger, formState, getFieldState, control } = useFormContext();
    const registerProps = register(name);
    const fieldState = getFieldState(name, formState);
    // Subscribe to this field's value via useWatch so the displayed selection re-renders when
    // it's updated externally (e.g. setValue from a parent effect, or initial form rehydration).
    // Using getValues here would only snapshot at render — leaving the Select stale until
    // some unrelated field change forces a re-render.
    const currentValue = useWatch({ control, name });

    const onChange = useCallback(
        (newValue: any) => {
            setValue(name, newValue);
            trigger(name);
        },
        [name, setValue, trigger]
    );

    return (
        <Select
            {...props}
            {...registerProps}
            errorElement={fieldState.error?.message as any}
            inputState={getInputState(fieldState, formState)}
            onChange={onChange}
            value={currentValue}
        />
    );
};

export default SelectField;
